import {useCallback, useState} from 'react';
import {Row, TableEditRowProps} from './types';
import TableEditCell from './TableEditCell';
import {NewTableCell} from './NewTableCell';
import {TableEditRowActions} from './TableEditRowActions';
import {useTableColumnRules} from 'utils/hooks/useTableColumnRules';
import {checkIsTableRowValid} from 'utils/functions/checkIsTableRowValid';
import {useTableCellValidation} from 'utils/hooks/useTableCellValidation';

const TableEditRow = <T extends Row>({
    onClose,
    columns,
    row,
    tableConfig,
    isItemSelected,
    onCreate,
    onEdit,
    requiredConfig,
    draggableProvided,
    dynamicValidationConfig,
    styleProps,
}: TableEditRowProps<T>) => {
    const [values, setValues] = useState<T>(row);
    const {setCellError, hasTableErrors} = useTableCellValidation();

    const onSubmit = () => {
        if (row?.id) {
            onEdit(values);
        } else {
            onCreate(values);
        }
    };

    const setValue = useCallback((key: string, newValue: any) => {
        if (Array.isArray(newValue)) {
            setValues((_prev) => ({
                ..._prev,
                [key]: newValue.filter((str) => str.trim() !== ''),
            }));
        } else {
            setValues((_prev) => ({..._prev, [key]: newValue}));
        }
    }, []);

    const {currentRequiredConfig, currentColumns} = useTableColumnRules<T>({
        dynamicValidationConfig,
        values,
        setValue,
        requiredConfig,
        columns,
    });

    const isValid = checkIsTableRowValid(currentRequiredConfig, values) && !hasTableErrors;

    return (
        <>
            <TableEditRowActions<T>
                tableConfig={tableConfig}
                isItemSelected={isItemSelected}
                onClose={onClose}
                onSubmit={onSubmit}
                isValid={isValid}
                draggableProvided={draggableProvided}
                styleProps={styleProps}
            />
            {Object.values(currentColumns).map((column) => {
                return (
                    <NewTableCell
                        row={values}
                        column={column}
                        key={`${row.id}_${column.id}`}
                    >
                        {!column?.readOnly && (
                            <TableEditCell
                                column={column}
                                values={values}
                                setValue={setValue}
                                requiredConfig={currentRequiredConfig}
                                initialDictKeyValue={
                                    column.dictKeyProperty &&
                                    (row as Record<string, any>)[column.dictKeyProperty]
                                }
                                onValidationErrorChange={(error) => setCellError(column.id, error)}
                            />
                        )}
                    </NewTableCell>
                );
            })}
        </>
    );
};

export default TableEditRow;
